import { useId, useMemo, useState, type FormEvent } from "react";
import { LoginRequestSchema, RegisterRequestSchema } from "@scrile/contracts";
import { describeError, isApiError } from "../api/errors";
import { useApi } from "../api/context";
import { Avatar } from "../components/Avatar";
import { Link } from "../components/Link";
import { t, tNodes, type MessageKey } from "../i18n";
import { deviceTimeZone } from "../lib/format";
import { rememberCode, takeCode } from "../lib/landing";
import { href, navigate } from "../lib/router";
import { useHeroData } from "../state/heroData";

type Mode = "register" | "login";
type Field = "email" | "password" | "displayName" | "ageConfirmed" | "code";
type FieldErrors = Partial<Record<Field, string>>;

const FIELD_TEXTS: Record<Field, MessageKey> = {
  email: "auth.invalid.email",
  password: "auth.invalid.password",
  displayName: "auth.invalid.name",
  ageConfirmed: "auth.invalid.age",
  code: "auth.invalid.code",
};

/**
 * Ошибки полей — своими словами из словаря, по имени поля. Одинаково для проверки в
 * браузере (Zod-схема контракта) и для issues из ответа сервера: путь бывает «email»
 * или «body.email», важен последний сегмент.
 */
function fieldErrors(paths: readonly (string | number | undefined)[], mode: Mode): FieldErrors {
  const found: FieldErrors = {};
  for (const raw of paths) {
    const name = String(raw ?? "").split(".").pop() ?? "";
    if (!(name in FIELD_TEXTS)) continue;
    const field = name as Field;
    found[field] = mode === "login" && field === "password" ? t("auth.invalid.passwordEmpty") : t(FIELD_TEXTS[field]);
  }
  return found;
}

/**
 * Регистрация и вход. Аккаунт — платформенный, но экран — героя: его портрет и его
 * имя в подсказке. Имя нужно не для галочки: герой произносит его в сериях.
 */
export function Auth({ mode, next }: { mode: Mode; next: string | null }) {
  const api = useApi();
  const { hero, setMe } = useHeroData();
  const formId = useId();
  const timezone = useMemo(() => deviceTimeZone(), []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [code, setCode] = useState(() => takeCode() ?? "");
  const [codeOpen, setCodeOpen] = useState(() => (takeCode() ?? "") !== "");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isRegister = mode === "register";
  const destination = next ?? href.listen();

  function validate(): boolean {
    const parsed = isRegister
      ? RegisterRequestSchema.safeParse({
          email: email.trim(),
          password,
          displayName: displayName.trim(),
          ageConfirmed,
          timezone,
          ...(code.trim() ? { code: code.trim() } : {}),
        })
      : LoginRequestSchema.safeParse({ email: email.trim(), password });
    const found = parsed.success ? {} : fieldErrors(parsed.error.issues.map((issue) => issue.path.join(".")), mode);
    setErrors(found);
    return parsed.success;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!validate()) return;
    setBusy(true);
    try {
      const trimmedCode = code.trim();
      const me = isRegister
        ? await api.register({
            email: email.trim(),
            password,
            displayName: displayName.trim(),
            ageConfirmed: true,
            timezone,
            ...(trimmedCode ? { code: trimmedCode.toUpperCase() } : {}),
          })
        : await api.login({ email: email.trim(), password });
      // Код идёт и в оплату (CheckoutRequest.code): бонус к пробному периоду считает сервер
      if (isRegister) rememberCode(trimmedCode);
      setMe(me);
      navigate(destination, { replace: true });
    } catch (error) {
      // Сервер указал поля — подсвечиваем их; общий текст — только если полей нет
      const fromServer = isApiError(error, "validation") ? fieldErrors(error.issues.map((issue) => issue.path), mode) : {};
      if (Object.keys(fromServer).length > 0) setErrors(fromServer);
      else setFormError(describeError(error, isRegister ? "register" : "login"));
      setBusy(false);
    }
  }

  const fieldProps = (field: Field) => ({
    "aria-invalid": errors[field] ? true : undefined,
    "aria-describedby": `${formId}-${field}-hint`,
  });

  return (
    <main className="container narrow auth">
      <div className="auth__head">
        <Avatar src={hero.theme.portraitUrl} name={hero.name} size={64} />
        <h1>{isRegister ? t("auth.register.title") : t("auth.login.title")}</h1>
        <p className="muted">{isRegister ? t("auth.register.subtitle") : t("auth.login.subtitle", { hero: hero.name })}</p>
      </div>

      <form className="form panel" onSubmit={submit} noValidate>
        {isRegister && (
          <div className="field">
            <label htmlFor={`${formId}-name`}>{t("auth.name")}</label>
            <input
              id={`${formId}-name`}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="given-name"
              maxLength={40}
              required
              {...fieldProps("displayName")}
            />
            <p className={errors.displayName ? "field__error" : "field__hint"} id={`${formId}-displayName-hint`}>
              {errors.displayName ?? t("auth.nameHint")}
            </p>
            {!errors.displayName && <p className="field__hint">{t("auth.nameRule")}</p>}
          </div>
        )}

        <div className="field">
          <label htmlFor={`${formId}-email`}>{t("auth.email")}</label>
          <input
            id={`${formId}-email`}
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            {...fieldProps("email")}
          />
          {errors.email && (
            <p className="field__error" id={`${formId}-email-hint`}>
              {errors.email}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor={`${formId}-password`}>{t("auth.password")}</label>
          <input
            id={`${formId}-password`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isRegister ? "new-password" : "current-password"}
            minLength={isRegister ? 8 : undefined}
            required
            {...fieldProps("password")}
          />
          {(errors.password || isRegister) && (
            <p className={errors.password ? "field__error" : "field__hint"} id={`${formId}-password-hint`}>
              {errors.password ?? t("auth.passwordHint")}
            </p>
          )}
        </div>

        {isRegister && (
          <>
            {codeOpen ? (
              <div className="field">
                <label htmlFor={`${formId}-code`}>
                  {t("auth.code")} <span className="muted">· {t("auth.codeOptional")}</span>
                </label>
                <input
                  id={`${formId}-code`}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={32}
                  {...fieldProps("code")}
                />
                <p className={errors.code ? "field__error" : "field__hint"} id={`${formId}-code-hint`}>
                  {errors.code ?? t("auth.codeHint")}
                </p>
              </div>
            ) : (
              <button type="button" className="link-btn" onClick={() => setCodeOpen(true)}>
                {t("auth.codeToggle")}
              </button>
            )}

            <label className={`check${errors.ageConfirmed ? " check--invalid" : ""}`}>
              <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} />
              <span>{t("auth.age")}</span>
            </label>
            {errors.ageConfirmed && <p className="field__error">{errors.ageConfirmed}</p>}

            <p className="hint">{t("auth.timezone", { tz: timezone.replace(/_/g, " ") })}</p>
          </>
        )}

        {formError && (
          <p className="form__error" role="alert">
            {formError}
          </p>
        )}

        <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={busy}>
          {busy ? t("app.loading") : isRegister ? t("auth.register.submit") : t("auth.login.submit")}
        </button>

        {isRegister && (
          <p className="hint center">
            {tNodes("auth.terms", {
              terms: <a href={hero.legal.termsUrl}>{t("auth.termsLink")}</a>,
              privacy: <a href={hero.legal.privacyUrl}>{t("auth.privacyLink")}</a>,
            })}
          </p>
        )}
      </form>

      <p className="center auth__switch">
        {isRegister ? t("auth.toLogin") : t("auth.toRegister")}{" "}
        <Link to={isRegister ? href.signin(next) : href.join(next)} replace>
          {isRegister ? t("auth.toLoginLink") : t("auth.toRegisterLink")}
        </Link>
      </p>
    </main>
  );
}
